import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
// @mui
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { Box, Dialog, Grid, Stack, Typography } from '@mui/material';
import { LiveChatWidget } from '@livechat/widget-react';
import ReactPixel from 'react-facebook-pixel';
// hooks
import useSettings from '../../hooks/useSettings';
import useResponsive from '../../hooks/useResponsive';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// config
import { HEADER, NAVBAR } from '../../config';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import useAuth from '../../hooks/useAuth';

//
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import Footer from './Footer';

import Image from '../../components/Image';

import { getGameCategsAndCompanies, getSiteSettings, getUserMemberBalance } from '../../redux/slices/lookup';

import useLocales from '../../hooks/useLocales';

import { usePageVisibility } from '../../hooks/usePageVisibility';
import WithdrawFormDialog from './header/withdraw/WithdrawFormDialog';
import { openLoginSignup } from '../../redux/slices/authPages';
import ContactPopup from './ContactPopup';
import AccountProfile from '../../views/user/AccountProfile';
import CustomModal from '../../components/CustomModal';
import { setIsAccountProfileOpen, setIsDownloadHeaderOpen, setIsOpenCS } from '../../redux/slices/settings';
import Wallet from '../../views/user/Wallet';
import { openTransactionsDialog } from '../../redux/slices/transactionsDialog';
import History from '../../views/history/History';
import Iconify from '../../components/Iconify';

// ----------------------------------------------------------------------

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ showDownloadApp, isDesktop, collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: `calc(${HEADER.MOBILE_HEIGHT}px + ${showDownloadApp && !isDesktop ? 15 : 0}vw)`,
  paddingBottom: '74px',
  background: 'rgb(3, 7, 24)',
  // backgroundImage: `url(${require('../../assets/bg1.webp')})`,
  // backgroundAttachment: 'fixed',
  // backgroundRepeat: 'no-repeat',
  position: 'relative',
  zIndex: 0,
  '::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(180deg, rgb(72,69,106, .01) 15.6%, #201E36 100%)',
    opacity: 0.7,
    zIndex: -1,
  },
  // [theme.breakpoints.down('sm')]: {
  //   paddingTop: showDownloadApp && !isDesktop ? `cacl(${HEADER.MOBILE_HEIGHT}px + 95vw)` : HEADER.MOBILE_HEIGHT,
  // },
  [theme.breakpoints.up('lg')]: {
    // paddingLeft: 8,
    // paddingRight: 8,
    paddingTop: `calc(${HEADER.DASHBOARD_DESKTOP_HEIGHT}px + 3rem)`,
    // paddingBottom: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    // ...(collapseClick && {
    //   marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    // }),
  },
  [theme.breakpoints.up('sm')]: {
    backgroundPosition: '10% 78%',
    backgroundSize: '100%',
  },
  [theme.breakpoints.down('sm')]: {
    backgroundPosition: '10% 10%',
    backgroundSize: '100% 100%',
  },
}));

const BottomNav = styled(Box)(({ theme }) => ({
  // [theme.breakpoints.up('lg')]: {
  //   width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
  //   marginLeft: `calc(${NAVBAR.DASHBOARD_WIDTH}px)`,
  // },
}));

const BottomNavIcon = styled(Image)(({ theme }) => ({
  width: 35,
  height: 35,
  objectFit: 'contain',
}));

const BottomNavText = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  lineHeight: 1,
  marginTop: 2,
  fontFamily: 'sans-serif',
  whiteSpace: 'nowrap',
  color: '#b5b5b5',
  fontWeight: 600,
  zIndex: 1,
}));

const MenuBox = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexDirection: 'column',
  flex: 1,
}));

// ----------------------------------------------------------------------

const DashboardLayout = () => {
  const { isAuthenticated } = useAuth();

  const { collapseClick, isCollapse, onToggleCollapse } = useCollapseDrawer();

  const { isAddMemberBankDialog } = useSelector((x) => x.addMemberBankDialog);

  const { siteSettings } = useSelector((x) => x.lookup);

  const { isDownloadHeaderOpen, isAccountProfileOpen, isOpenCS } = useSelector((x) => x.settings);

  const { isOpenTransactionsDialog } = useSelector((x) => x.transactionsDialog);

  const isVisible = usePageVisibility();

  const { translate } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { themeLayout } = useSettings();

  const isDesktop = useResponsive('up', 'sm');

  const [open, setOpen] = useState(false);

  const [openContactsPopup, setOpenContactsPopup] = useState(false);

  const [openHistory, setOpenHistory] = useState(false);

  const verticalLayout = themeLayout === 'vertical';

  const { pathname } = useLocation();

  const isHome = pathname === '/home';

  useEffect(() => {
    // Check if the PWA is already installed (usually stored in localStorage or can be checked with appinstalled event)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      dispatch(setIsDownloadHeaderOpen(false));
    }

    // Listen for the `appinstalled` event
    window.addEventListener('appinstalled', () => {
      dispatch(setIsDownloadHeaderOpen(false));
    });

    return () => {
      window.removeEventListener('appinstalled', () => {
        dispatch(setIsDownloadHeaderOpen(false));
      });
    };
  }, []);

  useEffect(() => {
    const promises = [getGameCategsAndCompanies(), getSiteSettings()];
  }, [isAuthenticated]);

  /**
   * Call getUserMemberBalance() when tab is active and isAuthenticated
   * to keep the member balance updated
   */
  useEffect(() => {
    if (isVisible && isAuthenticated) getUserMemberBalance();
  }, [isVisible, isAuthenticated]);

  useEffect(() => {
    try {
      if (siteSettings?.config_analytics) {
        const _ga = siteSettings?.config_analytics?.split(',');

        // Multiple products (previously known as trackers)
        ReactGA.initialize(
          _ga.map((item) => ({
            // measurement ID
            trackingId: item,
            // gaOptions: {...}, // optional
            // gtagOptions: {...}, // optional
          }))
          //   [
          //   {
          //     // measurement ID
          //     trackingId: _ga[0],
          //     // gaOptions: {...}, // optional
          //     // gtagOptions: {...}, // optional
          //   },
          // ]
        );
      }

      // Comment out respond.io code as per Mike
      // if (siteSettings?.config_livechat) {
      //   const respondIo = JSON.parse(siteSettings?.config_livechat);
      //   const script = document.createElement('script');

      //   script.id = respondIo?.id;
      //   script.src = respondIo?.src;
      //   script.async = true;

      //   document.head.appendChild(script);
      // }

      if (siteSettings?.config_fb_pixel) {
        const metaPixel = siteSettings.config_fb_pixel.replace(/[^\d,]+/g, '').split(',');
        const options = {
          autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
          debug: false, // enable logs
        };

        metaPixel.forEach((_pixel) => {
          ReactPixel.init(_pixel, options);

          ReactPixel.pageView(); // For tracking page view
        });
      }
    } catch (error) {
      console.warn(`3RD PARTY INTEGRATION ERROR: ${error?.message}`);
    }
  }, [siteSettings]);

  // const getAllGames = () => {
  //   conObj.post('games/all.json?limit=1').then((res) => {
  //     if (res.status === 200) {
  //       // res.data?.data?.data
  //       // set appversion to localstorage
  //       // res.data?.data.appversion
  //       const _allGames = res.data?.data?.data;

  //       dispatch(
  //         setGameCategories(
  //           _allGames
  //             .filter((f) => f.key.toLowerCase().indexOf('gcat') !== -1)
  //             .map((item) => ({ key: item.key, name: item.name, slug: item.slug }))
  //         )
  //       );
  //     }
  //   });
  // };

  if (verticalLayout) {
    return (
      <>
        <DashboardHeader onOpenSidebar={() => setOpen(true)} verticalLayout={verticalLayout} />

        {/* {isDesktop ? (
          <NavbarHorizontal />
        ) : (
          <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
        )} */}

        <Box
          component="main"
          sx={{
            px: { lg: 2 },
            pt: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 80}px`,
            },
            pb: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${HEADER.DASHBOARD_DESKTOP_HEIGHT + 24}px`,
            },
          }}
        >
          <Outlet />
        </Box>
      </>
    );
  }

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
        // backgroundColor: '#0b1712',
      }}
    >
      <DashboardHeader
        isCollapse={isCollapse}
        isOpenSidebar={open}
        onOpenSidebar={() => setOpen(true)}
        onCloseSidebar={() => setOpen(!open)}
        onToggleCollapse={onToggleCollapse}
      />

      <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} setOpenHistory={setOpenHistory} />

      <MainStyle collapseClick={collapseClick} showDownloadApp={isDownloadHeaderOpen} isDesktop={isDesktop}>
        {/* {!isHome && <SubHeaderNav />} */}

        {/* <img src={require('../../assets/bg.png')} style={{ position: 'absolute' }} alt="" /> */}
        <Outlet />
        <Footer />
        {/* BOTTOM NAVIGATION */}
        <BottomNav
          sx={{
            width: '100%',
            height: '60px',
            // backgroundImage: `url(/assets/bottom-menu-bg.svg)`,
            // backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            // backgroundColor: palette.bottomMenu.bgColor,
            position: 'fixed',
            bottom: 0,
            left: 2,
            zIndex: 100,
            display: 'block',
            // p: { xs: '4px 4px 0 4px', md: '20px 20px 0 20px' },
            right: 2,
          }}
        >
          <Image src={'/assets/bottom-menu-bg.svg'} sx={{ height: '100%', width: '100%', position: 'absolute' }} />

          <Grid
            container
            sx={{
              // backgroundColor: palette.bottomMenu.bgColor,
              // boxShadow: '0px 0px 8px 0px #FFD633',
              borderRadius: '10px',
              height: '60px',
            }}
          >
            <MenuBox onClick={() => setOpen(true)} item xs={3}>
              <BottomNavIcon src={require('../../assets/mobile-menu/menu.png')} alt="Menu" />
              <BottomNavText>{translate('menu')}</BottomNavText>
            </MenuBox>

            <MenuBox
              onClick={() => {
                if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
                else dispatch(openTransactionsDialog({ open: true, isDeposit: true }));
              }}
              item
              xs={3}
            >
              <BottomNavIcon src={require('../../assets/mobile-menu/deposit.png')} alt="Slot" />
              <BottomNavText>{translate('deposit')}</BottomNavText>
            </MenuBox>

            {/* LIVE CASINO */}
            <MenuBox
              item
              xs={3}
              onClick={() => {
                if (!isAuthenticated) dispatch(openLoginSignup({ open: true, isLogin: true }));
                else dispatch(openTransactionsDialog({ open: true, isDeposit: false }));
              }}
            >
              <BottomNavIcon src={require('../../assets/mobile-menu/withdraw.png')} alt="Live Casino" />
              <BottomNavText>{translate('withdraw')}</BottomNavText>
            </MenuBox>

            <MenuBox item xs={3} onClick={() => dispatch(setIsOpenCS(true))}>
              <BottomNavIcon src={require('../../assets/mobile-menu/contact.png')} alt="Promotion" />
              <BottomNavText>{translate('contact')}</BottomNavText>
            </MenuBox>
          </Grid>
        </BottomNav>
      </MainStyle>

      {siteSettings?.config_livechat && siteSettings?.config_livechat?.length === 8 ? (
        <LiveChatWidget
          license={siteSettings?.config_livechat}
          onReady={(data) => {
            const widgetContainer = document.getElementById('chat-widget-container');

            if (widgetContainer && data.state.visibility === 'minimized') {
              widgetContainer.classList.add('adjust-bottom-space');
            }
          }}
          onVisibilityChanged={(data) => {
            const widgetContainer = document.getElementById('chat-widget-container');

            if (widgetContainer) {
              if (data.visibility === 'maximized') {
                widgetContainer.classList.remove('adjust-bottom-space');
              } else widgetContainer.classList.add('adjust-bottom-space');
            }
          }}
        />
      ) : (
        <></>
      )}

      {isAddMemberBankDialog?.open && <WithdrawFormDialog />}

      {isOpenCS && (siteSettings?.config_telegram || siteSettings?.config_line) && (
        <ContactPopup telegram={siteSettings?.config_telegram} line={siteSettings?.config_line} />
      )}

      <CustomModal open={isAccountProfileOpen} onClose={() => dispatch(setIsAccountProfileOpen(false))}>
        <AccountProfile />
      </CustomModal>

      <CustomModal
        open={isOpenTransactionsDialog?.open}
        onClose={() => dispatch(openTransactionsDialog({ open: false, isDeposit: true }))}
        maxWidth={'lg'}
        PaperPropsSx={{ maxWidth: 'unset', overflowY: { xs: 'scroll', sm: 'visible' }, position: 'relative' }}
        fullWidth={false}
        fullScreen={!isDesktop}
      >
        <Wallet />
      </CustomModal>

      {openHistory && (
        <Dialog fullScreen open={openHistory} onClose={() => setOpenHistory(false)}>
          <Stack direction="row" sx={{ background: 'rgb(2, 19, 44)', px: '.5rem', py: '.75rem' }}>
            <Iconify
              onClick={() => setOpenHistory(false)}
              icon={'ic:twotone-arrow-back'}
              sx={{ width: '26px', height: '26px', color: '#fff', cursor: 'pointer' }}
            />
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', textAlign: 'center', flex: 1 }}>
              {translate('transaction_history')}
            </Typography>
          </Stack>
          <History />
        </Dialog>
      )}
    </Box>
  );
};

export default DashboardLayout;
